<admin-app-header></admin-app-header>

<div class="marketing-health-page">
    <div class="container-fluid">
        <!-- Page Header -->
        <div class="page-header">
            <div class="page-header__title">
                <h1><i class="fa-solid fa-chart-line"></i> Marketing Health</h1>
                <p class="page-header__subtitle">Google Ads campaign performance, lead generation, and conversion tracking</p>
            </div>
        </div>

        <!-- Date Range Filter + Ad Spend -->
        <div class="row mb-4">
            <form class="row align-items-end" [formGroup]="filterForm">
                <div class="col-md-2">
                    <label for="filterDateRange" class="form-label">Date Range</label>
                    <select class="form-select" formControlName="filterDateRange">
                        <option *ngFor="let option of dateRangeOptions" [value]="option.value">{{ option.label }}</option>
                    </select>
                </div>

                <div class="col-md-2" *ngIf="isCustomDateRange">
                    <label for="customStartDate" class="form-label">Start Date</label>
                    <input type="date" class="form-control" formControlName="customStartDate" />
                </div>

                <div class="col-md-2" *ngIf="isCustomDateRange">
                    <label for="customEndDate" class="form-label">End Date</label>
                    <input type="date" class="form-control" formControlName="customEndDate" />
                </div>

                <div class="col-md-2">
                    <label class="form-label">Google Ads Spend (R)</label>
                    <input type="number" class="form-control" placeholder="e.g. 5000" (input)="onAdSpendChange($any($event.target).value)" />
                </div>
            </form>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading marketing data...</p>
        </div>

        <!-- Dashboard Content -->
        <div *ngIf="!isLoading">
            <!-- KPI Cards Row 1: Lead Generation -->
            <div class="summary-cards summary-cards--four">
                <div class="summary-card summary-card--info">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-users"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ totalLeads | number }}</span>
                        <span class="summary-card__label">Total Leads</span>
                        <span class="summary-card__subtext">All sources, {{ dateRangeLabel }}</span>
                    </div>
                </div>

                <div class="summary-card summary-card--google">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-bullhorn"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ googleAdsLeadCount | number }}</span>
                        <span class="summary-card__label">Google Ads Leads</span>
                        <span class="summary-card__subtext">{{ googleAdsPercentage | number: '1.1-1' }}% of all leads</span>
                    </div>
                </div>

                <div class="summary-card summary-card--success">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-check-circle"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ googleAdsConversions | number }}</span>
                        <span class="summary-card__label">Conversions</span>
                        <span class="summary-card__subtext" *ngIf="googleAdsTotalSubscriptions > googleAdsConversions">{{ googleAdsTotalSubscriptions }} subscriptions ({{ googleAdsTotalSubscriptions - googleAdsConversions }} repeat)</span>
                        <span class="summary-card__subtext" *ngIf="googleAdsTotalSubscriptions <= googleAdsConversions">{{ googleAdsTotalSubscriptions }} subscription{{ googleAdsTotalSubscriptions !== 1 ? 's' : '' }} from Google Ads leads</span>
                    </div>
                </div>

                <div class="summary-card summary-card--purple">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-percent"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ googleAdsConversionRate | number: '1.1-1' }}%</span>
                        <span class="summary-card__label">Conversion Rate</span>
                        <span class="summary-card__subtext" *ngIf="costPerLead !== null">CPL: {{ costPerLead | currency: 'R ':'symbol':'1.0-0' }} &middot; CPA: {{ costPerAcquisition !== null ? (costPerAcquisition | currency: 'R ':'symbol':'1.0-0') : 'N/A' }}</span>
                        <span class="summary-card__subtext" *ngIf="costPerLead === null">Google Ads lead to subscription</span>
                    </div>
                </div>
            </div>

            <!-- KPI Cards Row 2: Value -->
            <div class="summary-cards summary-cards--three">
                <div class="summary-card summary-card--warning">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-receipt"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ actualSubscriptionValue | currency: 'R ':'symbol':'1.0-0' }}</span>
                        <span class="summary-card__label">Actual Subscription Value</span>
                        <span class="summary-card__subtext">Total client payments from converted Google Ads leads</span>
                    </div>
                </div>

                <div class="summary-card summary-card--profit">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-chart-line"></i>
                    </div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ netRevenue | currency: 'R ':'symbol':'1.0-0' }}</span>
                        <span class="summary-card__label">Net Revenue</span>
                        <span class="summary-card__subtext">Service fee (per merchant) minus gateway fees ({{ globalVariablesService.currentVariables?.paygateGatewayFeePercentage ?? 3.5 }}%)</span>
                    </div>
                </div>

                <div class="summary-card" [ngClass]="returnOnAdSpend !== null ? (returnOnAdSpend! >= 1 ? 'summary-card--success' : 'summary-card--danger') : 'summary-card--muted'">
                    <div class="summary-card__icon">
                        <i class="fa-solid fa-scale-balanced"></i>
                    </div>
                    <div class="summary-card__content" *ngIf="adSpend">
                        <span class="summary-card__value">{{ returnOnAdSpend | number: '1.1-1' }}x</span>
                        <span class="summary-card__label">Return on Ad Spend</span>
                        <span class="summary-card__subtext">
                            CPL: {{ costPerLead | currency: 'R ':'symbol':'1.0-0' }} &middot;
                            CPA: {{ costPerAcquisition !== null ? (costPerAcquisition | currency: 'R ':'symbol':'1.0-0') : 'N/A' }}
                        </span>
                    </div>
                    <div class="summary-card__content" *ngIf="!adSpend">
                        <span class="summary-card__value">--</span>
                        <span class="summary-card__label">Return on Ad Spend</span>
                        <span class="summary-card__subtext">Enter ad spend above to calculate</span>
                    </div>
                </div>
            </div>

            <!-- Charts 2x2 Grid -->
            <div class="chart-grid">
                <!-- 1. Leads by Source -->
                <div class="chart-block">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-globe"></i>
                        <h3>Leads by Source</h3>
                    </div>
                    <div class="chart-block__body" *ngIf="sourceChart.series.length > 0 && sourceChart.categories.length > 0">
                        <apx-chart
                            [series]="sourceChart.series"
                            [chart]="barChartConfig"
                            [plotOptions]="{ bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: sourceChart.categories }"
                            [colors]="sourceChart.colors || ['#3e8bff']"
                            [dataLabels]="dataLabels"
                            [legend]="{ show: false }"
                        ></apx-chart>
                    </div>
                    <div class="chart-block__empty" *ngIf="sourceChart.categories.length === 0">
                        <p>No source data available</p>
                    </div>
                </div>

                <!-- 2. Monthly Trend -->
                <div class="chart-block">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-chart-bar"></i>
                        <h3>Monthly Trend</h3>
                    </div>
                    <div class="chart-block__body" *ngIf="trendChart.categories.length > 1">
                        <apx-chart
                            [series]="trendChart.series"
                            [chart]="trendChartConfig"
                            [plotOptions]="{ bar: { horizontal: false, columnWidth: '60%', borderRadius: 4 } }"
                            [xaxis]="{ categories: trendChart.categories }"
                            [colors]="trendChart.colors || ['#94a3b8', '#4285f4', '#4bd181']"
                            [dataLabels]="{ enabled: false }"
                            [legend]="{ position: 'top' }"
                        ></apx-chart>
                    </div>
                    <div class="chart-block__empty" *ngIf="trendChart.categories.length <= 1">
                        <p>Need more than one month of data for trend</p>
                    </div>
                </div>

                <!-- 3. Lead Status (Pipeline) -->
                <div class="chart-block">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-filter"></i>
                        <h3>Google Ads Lead Status</h3>
                    </div>
                    <div class="chart-block__body" *ngIf="funnelChart.series.length > 0 && funnelChart.categories.length > 0">
                        <apx-chart
                            [series]="funnelChart.series"
                            [chart]="barChartConfig"
                            [plotOptions]="{ bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: funnelChart.categories }"
                            [colors]="funnelChart.colors || ['#3e8bff']"
                            [dataLabels]="dataLabels"
                            [legend]="{ show: false }"
                        ></apx-chart>
                    </div>
                    <div class="chart-block__empty" *ngIf="funnelChart.categories.length === 0">
                        <p>No Google Ads leads in this period</p>
                    </div>
                </div>

                <!-- 4. Lead Assignee -->
                <div class="chart-block">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-user-check"></i>
                        <h3>Google Ads Lead Assignee</h3>
                    </div>
                    <div class="chart-block__body" *ngIf="assigneeChart.series.length > 0 && assigneeChart.categories.length > 0">
                        <apx-chart
                            [series]="assigneeChart.series"
                            [chart]="barChartConfig"
                            [plotOptions]="{ bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: assigneeChart.categories }"
                            [colors]="assigneeChart.colors || ['#3e8bff']"
                            [dataLabels]="dataLabels"
                            [legend]="{ show: false }"
                        ></apx-chart>
                    </div>
                    <div class="chart-block__empty" *ngIf="assigneeChart.categories.length === 0">
                        <p>No Google Ads leads in this period</p>
                    </div>
                </div>
            </div>

            <!-- Source Comparison Table -->
            <div class="table-block">
                <div class="table-block__header">
                    <i class="fa-solid fa-table"></i>
                    <h3>Source Comparison</h3>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th class="text-end">Leads</th>
                                <th class="text-end">Conversions</th>
                                <th class="text-end">Subs</th>
                                <th class="text-end">Conv. Rate</th>
                                <th class="text-end">Bays</th>
                                <th class="text-end">Sub. Value</th>
                                <th class="text-end">Net Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let row of sourceRows" [class.table-highlight]="row.isGoogleAds">
                                <td>
                                    <i *ngIf="row.isGoogleAds" class="fa-brands fa-google google-icon"></i>
                                    {{ row.source }}
                                </td>
                                <td class="text-end">{{ row.leads | number }}</td>
                                <td class="text-end">{{ row.conversions | number }}</td>
                                <td class="text-end">{{ row.subscriptions | number }}</td>
                                <td class="text-end">{{ row.conversionRate | number: '1.1-1' }}%</td>
                                <td class="text-end">{{ row.totalBays | number }}</td>
                                <td class="text-end">{{ row.actualValue | currency: 'R ':'symbol':'1.0-0' }}</td>
                                <td class="text-end">{{ row.netRevenue | currency: 'R ':'symbol':'1.0-0' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Campaign Breakdown Table -->
            <div class="table-block" *ngIf="campaignRows.length > 0">
                <div class="table-block__header">
                    <i class="fa-solid fa-flag"></i>
                    <h3>Google Ads Campaign Breakdown</h3>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th>Campaign</th>
                                <th class="text-end sortable" (click)="sortCampaignTable('leads')">
                                    Leads
                                    <i *ngIf="campaignSortColumn === 'leads'" [class]="campaignSortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i>
                                </th>
                                <th class="text-end sortable" (click)="sortCampaignTable('conversions')">
                                    Conversions
                                    <i *ngIf="campaignSortColumn === 'conversions'" [class]="campaignSortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i>
                                </th>
                                <th class="text-end sortable" (click)="sortCampaignTable('subscriptions')">
                                    Subs
                                    <i *ngIf="campaignSortColumn === 'subscriptions'" [class]="campaignSortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i>
                                </th>
                                <th class="text-end sortable" (click)="sortCampaignTable('conversionRate')">
                                    Conv. Rate
                                    <i *ngIf="campaignSortColumn === 'conversionRate'" [class]="campaignSortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i>
                                </th>
                                <th class="text-end sortable" (click)="sortCampaignTable('totalBays')">
                                    Bays
                                    <i *ngIf="campaignSortColumn === 'totalBays'" [class]="campaignSortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i>
                                </th>
                                <th class="text-end sortable" (click)="sortCampaignTable('actualValue')">
                                    Sub. Value
                                    <i *ngIf="campaignSortColumn === 'actualValue'" [class]="campaignSortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i>
                                </th>
                                <th class="text-end sortable" (click)="sortCampaignTable('netRevenue')">
                                    Net Revenue
                                    <i *ngIf="campaignSortColumn === 'netRevenue'" [class]="campaignSortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let row of campaignRows">
                                <td>{{ row.campaign }}</td>
                                <td class="text-end">{{ row.leads | number }}</td>
                                <td class="text-end">{{ row.conversions | number }}</td>
                                <td class="text-end">{{ row.subscriptions | number }}</td>
                                <td class="text-end">{{ row.conversionRate | number: '1.1-1' }}%</td>
                                <td class="text-end">{{ row.totalBays | number }}</td>
                                <td class="text-end">{{ row.actualValue | currency: 'R ':'symbol':'1.0-0' }}</td>
                                <td class="text-end">{{ row.netRevenue | currency: 'R ':'symbol':'1.0-0' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</div>
